import { Injectable } from '@angular/core';
import { CacheService } from 'projects/common/src/app/services';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  IChatFilter,
  IChatMessage,
  IChatMessageResponse,
  IChatSender,
  IGlobalMessage,
  IMachineMessage,
  IMachineMessageResponse,
} from '../interfaces/messenger';
import { catchError } from 'rxjs/operators';
import { CommunicationService } from 'src/app/services/communication';
import { AudioService } from './audio.service';
import { sortByKey, translateMachineName } from 'projects/common/src/app/services/utils/utilities';
import { Utility } from 'projects/common/src/app/classes/utility';
import * as moment from 'moment';
import { getMoment } from './datetime.service';
import { SoundService } from './parameter/sound/sound.service';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { UrlBuilderService } from 'projects/communication/src/app/services/url-builder.service';

@Injectable({
  providedIn: 'root',
})
export class MessengerService {
  private baseUrl: string;

  constructor(
    private _cacheService: CacheService,
    private _communication: CommunicationService,
    private _audioService: AudioService,
    private _soundService: SoundService,
    private _languageService: LanguageService,
    private _urlBuilderService: UrlBuilderService,
  ) {
    this.baseUrl = this._urlBuilderService.serverBasePath;
  }

  public getAvailableMachines(): Observable<IMachineMessageResponse[]> {
    const companyId = this._cacheService.local('session').user.company.id;
    const url = `${this.baseUrl}/Machine/GetMessengerMachineByCompany?companyId=${companyId}`;
    return this._communication.get(url).pipe(catchError((err) => throwError(() => err.error)));
  }

  public sendMessage(message: IChatSender): Observable<IChatMessage> {
    const url = `${this.baseUrl}/Messenger`;
    return this._communication.post(url, message).pipe(catchError((err) => throwError(() => err.error)));
  }

  public getMessagesByMachineId(machineId: number, page: number, pageSize: number): Observable<IChatMessageResponse> {
    const url = `${this.baseUrl}/Messenger/machine/${machineId}/paging/${page}/${pageSize}`;
    return this._communication.get(url).pipe(catchError((err) => throwError(() => err.error)));
  }

  public sendGlobalMessage(message: IGlobalMessage): Observable<any> {
    const companyId = this._cacheService.local('session').user.company.id;
    const url = `${this.baseUrl}/Messenger/global/${companyId}`;
    return this._communication.post(url, message).pipe(catchError((err) => throwError(() => err.error)));
  }

  public checkMessage(message: IChatMessage): Observable<any> {
    const url = `${this.baseUrl}/Messenger/check/${message.messageId}`;
    return this._communication.post(url, {}).pipe(catchError((err) => throwError(() => err.error)));
  }

  public getUnreadMessages(machines: IMachineMessage[]): IMachineMessage[] {
    return machines.filter((machine: IMachineMessage) => machine.unreadMessageQty > 0);
  }

  public handleMachineMessages(machines: IMachineMessageResponse[] | IMachineMessage[]): IMachineMessage[] {
    const { machinesWithChat, machinesWithoutChat } = [...machines].reduce(
      (accumulator, machine) => {
        if (machine.body && machine.body.length > 0) {
          accumulator.machinesWithChat.push(machine);
        } else {
          accumulator.machinesWithoutChat.push(machine);
        }
        return accumulator;
      },
      { machinesWithChat: [], machinesWithoutChat: [] },
    );

    const sortedMachines = [...sortByKey(machinesWithChat, 'createdAt', 'desc'), ...sortByKey(machinesWithoutChat, 'machineName')];

    const machinesMessageList: IMachineMessage[] = sortedMachines.map((machine: IMachineMessageResponse) => {
      const machineMessage: IMachineMessage = {
        machineId: machine.machineId,
        machineName: machine.machineName,
        machineTypeId: machine.machineTypeId,
        machineTypeName: translateMachineName(machine.machineTypeName, this._cacheService.local('session').user.company.id),
        statusName: machine.statusName ?? this._languageService.message('no-status-name', null),
        statusColor: machine.statusColor ?? '#ffcf39',
        statusFamilyName: machine.parentStatusImagePrefix,
        operatorName: machine.operatorName,
        body: machine.body,
        createdAt: machine.createdAt,
        unreadMessageQty: machine.unreadMessageQty,
        timeLapsed: this.checkTimeLapsed(machine.createdAt),
        imageKey: Utility.getSideMachineIconKey(machine.parentStatusImagePrefix, machine.machineIcon),
        zoneName: machine.zoneName,
        isConnected: machine.isConnected,
        readAt: machine.readAt,
        hourStart: machine.hourStart,
        fuelStart: machine.fuelStart,
        zoneParkId: machine.zoneParkId,
        zoneParkName: machine.zoneParkName,
        parentStatusImagePrefix: machine.parentStatusImagePrefix,
        machineIcon: machine.machineIcon,
        parentStatusSecondColor: machine.parentStatusSecondColor || '#433E3E',
        parentStatusThirdColor: machine.parentStatusThirdColor || '#ffffff',
      };
      return machineMessage;
    });

    return machinesMessageList;
  }

  public checkTimeLapsed(date: string): string {
    let timeLapsed = '';

    if (date) {
      const messages = [
        this._languageService.message('in_a_minute'),
        this._languageService.message('in_a_few_seconds'),
        this._languageService.message('a_few_seconds_ago'),
      ];

      timeLapsed = this.getTimeLapsedByLanguage(date);

      if (messages.includes(timeLapsed)) {
        timeLapsed = this._languageService.message('recently', null);
      }
    }

    return timeLapsed;
  }

  private getTimeLapsedByLanguage(date: string): string {
    return environment.lang === 'es-cl' ? moment(date).locale('es').fromNow() : moment(date).locale('en').fromNow();
  }

  public checkDelimiters(messages: IChatMessage[]): IChatMessage[] {
    let currentDay: moment.Moment;

    const list = [...messages].reverse().map((message) => {
      const messageObj = { ...message };

      const messageDate = getMoment(messageObj.createdAt.split('T')[0]);
      if (currentDay && currentDay.isAfter(messageDate, 'day')) {
        messageObj.hasDateDelimiter = true;

        if (getMoment(new Date()).subtract('1', 'day').format('DD/MM/YYYY') === messageDate.format('DD/MM/YYYY')) {
          messageObj.isYesterday = true;
        }
      }

      currentDay = messageDate;

      return messageObj;
    });

    return list.reverse();
  }

  public playNewMessageSound(): void {
    if (this._soundService.get()) {
      this._audioService.playSound('../../../../assets/sounds/new-message.mp3');
    }
  }

  public filterMessages(machineMessages: IMachineMessage[], chatFilter: IChatFilter): IMachineMessage[] {
    let filteredList = machineMessages;

    if (chatFilter.zoneParkIds?.length) {
      filteredList = filteredList.filter((x) => chatFilter.zoneParkIds.includes(x.zoneParkId));
    }

    if (chatFilter.machineTypeId) {
      filteredList = filteredList.filter((x) => x.machineTypeId === chatFilter.machineTypeId);
    }

    if (chatFilter.statusNames?.length) {
      filteredList = filteredList.filter((x) => chatFilter.statusNames.includes(x.statusName));
    }

    if (chatFilter.search) {
      filteredList = filteredList.filter(
        (x) =>
          x.operatorName?.toLowerCase().includes(chatFilter.search.toLowerCase()) ||
          x.machineName?.toLowerCase().includes(chatFilter.search.toLowerCase()),
      );
    }

    return filteredList;
  }

  public getInitialFilterState(): IChatFilter {
    return { search: '', zoneParkIds: null, statusNames: null, machineTypeId: null };
  }

  public getUnreadNotificationSidebar(unreadMessages: IMachineMessage[]): number {
    return unreadMessages.reduce((total, machine) => total + machine.unreadMessageQty, 0);
  }
}
